import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { getAllTemplatesCategoriesRoute, applyTemplateToFeatureRoute } from '../../api/routes';
import LottieIcon from '../common/LottieIcon';
import { getIconCategoryClass } from '../common/iconCategory';
import PopUpPanel from '../PopUpPanel/PopUpPanel';
import toDisplayAssetUrl from '../../helpers/DisplayAssetUrl';
import { getTemplateCategoryIds, templateHasCategory } from '../../helpers/templateCategories';
import './ApplyTemplatePopup.scss';

/**
 * Popup that lets users replace an existing campaign feature's settings with a template.
 *
 * Props:
 *  - campaignId         {string}   the campaign _id
 *  - campaignFeatureId  {string}   the campaign feature subdoc _id to replace
 *  - featureVarKey      {string}   varKey used to filter templates to this feature type
 *  - featureLabel       {string}   display name shown in the title
 *  - hasCustomisations  {boolean}  whether the feature already has saved customisations
 *  - onClose            {function} called when the popup should be dismissed
 *  - onApplied          {function(campaignData)} called after a template is successfully applied
 */
const ApplyTemplatePopup = ({
    campaignId,
    campaignFeatureId,
    featureVarKey,
    featureLabel,
    hasCustomisations,
    onClose,
    onApplied,
}) => {
    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]); // categories that have ≥1 template for this varKey
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set()); // empty = show all
    const [loading, setLoading] = useState(true);
    const [pendingTemplate, setPendingTemplate] = useState(null); // template chosen, awaiting confirm
    const [connectedFeatureConflict, setConnectedFeatureConflict] = useState(null);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(getAllTemplatesCategoriesRoute, { withCredentials: true })
            .then(res => {
                if (res.data.success) {
                    const data = res.data.data;
                    const all = (data.templates || []).filter(
                        t => t.templateType === 'feature' && t.primaryVarKey === featureVarKey
                    );
                    setTemplates(all);

                    // Build the set of category IDs that have at least one matching template
                    const usedCatIds = new Set(all.flatMap((template) => getTemplateCategoryIds(template)));
                    const relevantCats = (data.categories || []).filter(
                        c => usedCatIds.has(c._id) || usedCatIds.has(c.id)
                    );
                    setCategories(relevantCats);
                }
            })
            .catch(() => setError('Failed to load templates.'))
            .finally(() => setLoading(false));
    }, [featureVarKey]);

    const toggleCategory = (catId) => {
        setSelectedCategoryIds(prev => {
            const next = new Set(prev);
            if (next.has(catId)) {
                next.delete(catId);
            } else {
                next.add(catId);
            }
            return next;
        });
    };

    const visibleTemplates = selectedCategoryIds.size === 0
        ? templates
        : templates.filter((template) => [...selectedCategoryIds].some((categoryId) => templateHasCategory(template, categoryId)));

    const handleTemplateClick = (template) => {
        if (hasCustomisations) {
            // Show warning before applying
            setPendingTemplate(template);
        } else {
            applyTemplate(template);
        }
    };

    const applyTemplate = async (template, connectedFeatureMode = null) => {
        setApplying(true);
        setError(null);
        try {
            const res = await axios.post(applyTemplateToFeatureRoute, {
                campaignId,
                campaignFeatureId,
                templateId: template._id || template.id,
                connectedFeatureMode,
            }, {
                withCredentials: true,
                validateStatus: (status) => ((status >= 200 && status < 300) || status === 409),
            });

            if (res.data?.code === 'CONNECTED_TEMPLATE_FEATURE_CONFLICT') {
                setConnectedFeatureConflict({
                    template,
                    details: res.data.details || {},
                });
                setApplying(false);
                return;
            }

            if (res.data.success) {
                setConnectedFeatureConflict(null);
                onApplied && onApplied(res.data.campaignData);
            } else {
                setError(res.data.error || 'Failed to apply template.');
                setApplying(false);
            }
        } catch (err) {
            const responseData = err?.response?.data || {};
            if (err?.response?.status === 409 && responseData.code === 'CONNECTED_TEMPLATE_FEATURE_CONFLICT') {
                setConnectedFeatureConflict({
                    template,
                    details: responseData.details || {},
                });
                setApplying(false);
                return;
            }

            setError(responseData.error || err.message || 'Failed to apply template.');
            setApplying(false);
        }
    };

    if (connectedFeatureConflict) {
        const { template, details } = connectedFeatureConflict;
        const existingFeatureCount = details?.existingFeatureCount || 0;
        const connectedFeatureCount = details?.connectedFeatureCount || 0;

        return (
            <PopUpPanel showCloseBtn={!applying} closePanelFunc={onClose} className="apply-template-popup apply-template-popup--confirm">
                <div className="atp-header">
                    <h2 className="atp-title">Choose Connected Components</h2>
                </div>
                <div className="atp-warning-body">
                    <p className="atp-warning-text">
                        <strong>{template?.name}</strong> adds connected components like triggered audio or video.
                        This campaign already has <strong>{existingFeatureCount}</strong> connected component{existingFeatureCount === 1 ? '' : 's'} from the same template.
                    </p>
                    <p className="atp-warning-subtext">
                        Reuse existing components if you want this feature to share those connected items.
                        Create new components if you want this feature to get its own separate connected items so you can edit them independently.
                    </p>
                    <p className="atp-warning-subtext">
                        Connected components in this template: <strong>{connectedFeatureCount}</strong>
                    </p>
                </div>
                {error && <div className="atp-error">{error}</div>}
                <div className="atp-warning-actions">
                    <button
                        className="atp-btn atp-btn--secondary"
                        onClick={() => setConnectedFeatureConflict(null)}
                        disabled={applying}
                    >
                        ← Back
                    </button>
                    <button
                        className="atp-btn atp-btn--secondary"
                        onClick={() => applyTemplate(template, 'reuse')}
                        disabled={applying}
                    >
                        {applying ? 'Applying…' : 'Reuse Existing Components'}
                    </button>
                    <button
                        className="atp-btn atp-btn--danger"
                        onClick={() => applyTemplate(template, 'createNew')}
                        disabled={applying}
                    >
                        {applying ? 'Applying…' : 'Create New Components'}
                    </button>
                </div>
            </PopUpPanel>
        );
    }

    // ── Confirmation warning view ─────────────────────────────────────────────
    if (pendingTemplate) {
        return (
            <PopUpPanel showCloseBtn={!applying} closePanelFunc={onClose} className="apply-template-popup apply-template-popup--confirm">
                <div className="atp-header">
                    <h2 className="atp-title">Replace Feature Settings?</h2>
                </div>
                <div className="atp-warning-body">
                    {/* <div className="atp-warning-icon">⚠️</div> */}
                    <p className="atp-warning-text">
                        Applying <strong>{pendingTemplate.name}</strong> will
                        <strong> permanently overwrite all customisations</strong> for{' '}
                        <strong>{featureLabel || 'this feature'}</strong>. This cannot be undone.
                    </p>
                    <p className="atp-warning-subtext">
                        Any images, colours, text or other settings you have configured will be replaced
                        with the template defaults.
                    </p>
                </div>
                {error && <div className="atp-error">{error}</div>}
                <div className="atp-warning-actions">
                    <button
                        className="atp-btn atp-btn--secondary"
                        onClick={() => setPendingTemplate(null)}
                        disabled={applying}
                    >
                        ← Back
                    </button>
                    <button
                        className="atp-btn atp-btn--danger"
                        onClick={() => applyTemplate(pendingTemplate)}
                        disabled={applying}
                    >
                        {applying ? 'Applying…' : 'Yes, Replace Settings'}
                    </button>
                </div>
            </PopUpPanel>
        );
    }

    // ── Template picker view ──────────────────────────────────────────────────
    return (
        <PopUpPanel showCloseBtn closePanelFunc={onClose} className="apply-template-popup">
            <div className="atp-header">
                <h2 className="atp-title">Apply Template</h2>
                <p className="atp-subtitle">
                    Choose a template to apply to <strong>{featureLabel || 'this feature'}</strong>.
                    {hasCustomisations && (
                        <span className="atp-subtitle-warning"> Your current settings will be replaced.</span>
                    )}
                </p>
            </div>

            {loading && <div className="atp-loading">Loading templates…</div>}

            {!loading && templates.length === 0 && (
                <div className="atp-empty">No templates available for this feature type.</div>
            )}

            {/* Category filter lozenges — only when ≥2 categories exist */}
            {!loading && categories.length >= 2 && (
                <div className="atp-category-filters">
                    {categories.map(cat => {
                        const catId = cat._id || cat.id;
                        const isActive = selectedCategoryIds.has(catId);
                        return (
                            <button
                                key={catId}
                                className={`atp-cat-lozenge${isActive ? ' active' : ''}`}
                                onClick={() => toggleCategory(catId)}
                                type="button"
                            >
                                {cat.name}
                            </button>
                        );
                    })}
                </div>
            )}

            {!loading && templates.length > 0 && (
                <div className="atp-grid">
                    {visibleTemplates.length === 0 ? (
                        <div className="atp-empty atp-empty--inline">No templates in the selected categories.</div>
                    ) : visibleTemplates.map(t => {
                        const featVarKey = t.primaryVarKey || '';
                        const rawCat = Array.isArray(t.primaryFeatureCategory)
                            ? t.primaryFeatureCategory[0]
                            : (t.primaryFeatureCategory || 'misc');
                        const featCatSlug = getIconCategoryClass(rawCat);
                        return (
                            <div
                                key={t._id || t.id}
                                className="atp-card"
                                onClick={() => handleTemplateClick(t)}
                            >
                                <div className="atp-card-img-wrap">
                                    <div
                                        className="atp-card-img"
                                        style={
                                            t.templateImage
                                                ? { backgroundImage: `url(${toDisplayAssetUrl(t.templateImage)})` }
                                                : {}
                                        }
                                    />
                                    <div className={`atp-icon-badge category-icon-bg--${featCatSlug}`}>
                                        <LottieIcon varKey={featVarKey} size={20} />
                                    </div>
                                </div>
                                <div className="atp-card-label">{t.name}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {error && <div className="atp-error">{error}</div>}
        </PopUpPanel>
    );
};

export default ApplyTemplatePopup;
